import { Card, CardContent, Grid, Link, Stack, Typography } from '@mui/material';
import PageShell from '../components/layout/PageShell';

const sections = [
  {
    title: 'Data, alignment, and quantification',
    body: (
      <>
        Healthy endothelial RNA-seq runs were obtained from the European Nucleotide Archive. Reads were aligned to the GRCh38 Ensembl release 107 primary assembly with STAR 2.7.0e and quantified with Rsubread featureCounts 2.8.2. The curated web cohort contains 264 samples from 63 studies and 16 tissues, including Brain endothelium.
      </>
    ),
  },
  {
    title: 'Pairwise differential expression',
    body: (
      <>
        Brain endothelial samples were compared separately with each non-Brain endothelial tissue. DESeq2 used a zero-intercept design with layout and instrument variables; WRST used median-ratio-normalized counts as a complementary rank-based analysis. Adjusted p or FDR below 0.05 defined significance. The full scope contains 15 comparisons; the selected scope contains colon, heart, intestine, kidney, liver, lung, and vessel.
      </>
    ),
  },
  {
    title: 'Robust rank aggregation and validation',
    body: (
      <>
        The Transmembrane Focus integrates four Brain-overexpression lists—DESeq2 and WRST across full and selected scopes—using Robust Rank Aggregation (RRA). The displayed Four-list RRA score is −log10(RRA p). Method-specific badges indicate membership in separate DESeq2 or WRST RRA results. Bootstrap badges summarize RRA after 100 runs that each subsampled 60% of Brain samples without replacement. RRA p below 0.05 defined membership.
      </>
    ),
  },
  {
    title: 'Expression context',
    body: (
      <>
        Brain median count is calculated from the MRN-normalized analysis count matrix across 37 Brain samples. Brain detection is the percentage of those samples with a count greater than zero. The expression dialog presents sample-level log1p-transformed normalized counts across tissues; the table metrics remain untransformed.
      </>
    ),
  },
  {
    title: 'Proteomics cross-reference',
    body: (
      <>
        Fresh human BMEC mass-spectrometry data from PRIDE accession <Link href="https://www.ebi.ac.uk/pride/archive/projects/PXD018602" target="_blank" rel="noreferrer">PXD018602</Link> were processed with MaxQuant 2.3.0.0. After removal of contaminants and decoys, protein groups with Q below 0.01 were cross-referenced with Transmembrane Focus genes. A negative badge means the protein was not detected under this dataset and filtering workflow.
      </>
    ),
  },
  {
    title: 'Perfusion context',
    body: (
      <>
        For each of four method/scope configurations, perfusion rates were summed for heart, liver, kidney, lung, and skin comparisons where a gene was significantly Brain-overexpressed. A missing appearance contributes zero, and the four sums are averaged. Higher values indicate support against more highly perfused comparator tissues. This heuristic is contextual metadata rather than a measurement of gene or BBB perfusion and does not affect the four-list RRA ranking.
      </>
    ),
  },
];

export default function MethodsPage() {
  return (
    <PageShell
      eyebrow="Analysis reference"
      title="Methods"
      intro="This summary defines the calculations represented in Brendo. The manuscript and repository provide the complete analysis workflow and source data."
    >
      <Grid container spacing={3}>
        {sections.map((section) => (
          <Grid item xs={12} md={6} key={section.title}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 3.5 }}>
                <Typography component="h2" variant="h5" sx={{ mb: 1.5 }}>{section.title}</Typography>
                <Typography color="text.secondary" sx={{ lineHeight: 1.75 }}>{section.body}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Stack sx={{ mt: 5, p: 3, borderRadius: 2, bgcolor: '#eaf4f6' }} spacing={1}>
        <Typography sx={{ fontWeight: 800 }}>Numeric presentation</Typography>
        <Typography color="text.secondary">
          Continuous values are shown to three significant figures; detection is shown to one decimal percentage point. Sorting, filtering, and CSV export use the exact underlying values.
        </Typography>
      </Stack>
    </PageShell>
  );
}
